import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { checkModuleRight, useModuleRights } from '@work4all/data';

import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import { useToniAIApi } from '../hooks/use-toni-ai-api';
import {
  ToniAIEmailRequest,
  ToniAIEmailResponse,
  ToniAIMessageItem,
  ToniAIMessageType,
} from '../types';

export interface ToniAIContextValue {
  available: boolean;
  loading: boolean;
  isOpen: boolean;
  setOpen(open: boolean): void;
  width: number;
  setWidth(width: number): void;
  term: string;
  setTerm(term: string): void;
  eMailRequestData: Pick<ToniAIEmailRequest, 'email_body' | 'email_title'>;
  setEMailRequestData(
    data: Pick<ToniAIEmailRequest, 'email_body' | 'email_title'>
  ): void;
  threadId: string;
  setThreadId(id: string): void;
  triggerGetEmail(additionalTerm?: string): void;
  reset(): void;
  messages: ToniAIMessageItem[];
  currentStream: string;
}

const ToniAIContext = createContext<ToniAIContextValue | null>(null);

export function ToniAIProvider(props: React.PropsWithChildren<unknown>) {
  const { t } = useTranslation();

  const defaultMessages = useRef([
    {
      time: new Date(),
      type: ToniAIMessageType.INCOMING,
      incoming: {
        assembled_result: {
          response: t('TONI_AI.DEFAULT_MESSAGE'),
          proposed_subject: '',
          subject_should_change: false,
        },
        chunk: '',
        error: false,
        error_message: '',
        is_complete: true,
        thread_id: '',
      } as ToniAIEmailResponse,
    },
  ]);

  const [messages, setMessages] = useState<ToniAIMessageItem[]>(
    defaultMessages.current
  );

  const [isOpen, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [width, setWidth] = useState<number>(400);
  const [term, setTerm] = useState<string>('');
  const [currentStream, setCurrentStream] = useState<string>('');
  const currentStreamRef = useRef('');
  const [threadId, setThreadId] = useState<string>('');
  const [eMailRequestData, setEMailRequestData] =
    useState<Pick<ToniAIEmailRequest, 'email_body' | 'email_title'>>(null);

  const { getEmail } = useToniAIApi({ threadId });

  const { rights } = useModuleRights();
  const available = checkModuleRight(
    rights,
    ModuleAccessRightType.FEATURE_AI_TOOLS
  );

  const reset = useCallback(async () => {
    setLoading(false);
    setTerm('');
    setMessages(defaultMessages.current);
    setCurrentStream('');
    setThreadId('');
  }, []);

  const triggerGetEmail = useCallback(
    async (additionalTerm = '') => {
      setLoading(true);
      const request = {
        ...eMailRequestData,
        question: term + (additionalTerm ? ` ${additionalTerm}` : ''),
        thread_id: threadId,
      };

      const newMessages: ToniAIMessageItem[] = [
        ...messages,
        {
          time: new Date(),
          type: ToniAIMessageType.OUTGOING,
          outgoing: request,
        },
      ];

      setMessages(newMessages);

      getEmail(request).then((response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        function read() {
          return reader.read().then(({ done, value }) => {
            try {
              const response: ToniAIEmailResponse[] = decoder
                .decode(value, { stream: true })
                .split('\n')
                .filter(Boolean)
                .map((x) => JSON.parse(x));

              const latestItem = response[response.length - 1];

              if (latestItem?.thread_id) {
                setThreadId(latestItem.thread_id);
              }

              if (latestItem?.chunk) {
                currentStreamRef.current =
                  currentStreamRef.current +
                  response.map((x) => x.chunk).join('');
                setCurrentStream(currentStreamRef.current);
              }

              if (latestItem?.is_complete) {
                const finalMessages = [
                  ...newMessages,
                  {
                    time: new Date(),
                    type: ToniAIMessageType.INCOMING,
                    incoming: latestItem,
                  },
                ];
                setMessages(finalMessages);
              }

              if (done) {
                setLoading(false);
                currentStreamRef.current = '';
                setCurrentStream('');
                return;
              }
            } catch (error) {
              console.error(error);
            }
            return read();
          });
        }

        return read();
      });

      setTerm('');
    },
    [eMailRequestData, term, threadId, messages, getEmail]
  );

  const context: ToniAIContextValue = {
    isOpen,
    setOpen,
    width,
    setWidth,
    term,
    setTerm,
    threadId,
    setThreadId,
    triggerGetEmail,
    eMailRequestData,
    setEMailRequestData,
    messages,
    loading,
    currentStream,
    reset,
    available,
  };

  return (
    <ToniAIContext.Provider value={context}>
      {props.children}
    </ToniAIContext.Provider>
  );
}

export function useToniAIContext(): ToniAIContextValue {
  const context = useContext(ToniAIContext);

  if (context === null) {
    throw new Error(`useToniAIContext must be used within a <ToniAIContext>`);
  }

  return context;
}
